
import React from 'react';
import { HashRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './store';
import { UserRole } from './types';
import MarketingPage from './views/MarketingPage';
import Onboarding from './views/Onboarding';
import RequesterDashboard from './views/RequesterDashboard';
import ArtisanDashboard from './views/ArtisanDashboard';
import CreateRequest from './views/CreateRequest';
import RequestDetail from './views/RequestDetail';
import ActiveJobs from './views/ActiveJobs';
import Profile from './views/Profile';
import { Compass, Plus, ClipboardList, User, Hammer, LogOut } from 'lucide-react';

const Nav: React.FC = () => {
  const { currentUser, setCurrentUser } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  if (!currentUser || location.pathname === '/') return null;

  const dashboardPath = currentUser.role === UserRole.REQUESTER ? "/requester" : "/artisan";
  const isActive = (path: string) => location.pathname === path;

  const handleExit = () => {
    setCurrentUser(null);
    navigate('/');
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 glass border-t border-black/5 px-6 py-4 flex justify-between items-center z-[100] md:top-0 md:bottom-auto md:h-20 shadow-sm">
      <div className="hidden md:flex items-center gap-2 font-black text-2xl text-stone-900 tracking-tighter font-serif italic">
        <Hammer size={24} className="text-amber-600" />
        SkillLink
      </div>
      <div className="flex w-full justify-around md:w-auto md:gap-10 lg:gap-12 items-center">
        <Link 
          to={dashboardPath} 
          className={`flex flex-col items-center gap-1 transition-all ${isActive(dashboardPath) ? 'text-amber-700 scale-110' : 'text-stone-400 hover:text-stone-900'}`}
        >
          <Compass size={22} strokeWidth={isActive(dashboardPath) ? 2.5 : 2} />
          <span className="text-[10px] font-bold uppercase tracking-widest hidden md:block">Explore</span>
        </Link>
        {currentUser.role === UserRole.REQUESTER && (
          <Link 
            to="/create" 
            className={`flex flex-col items-center gap-1 transition-all ${isActive('/create') ? 'text-amber-700 scale-110' : 'text-stone-400 hover:text-stone-900'}`}
          >
            <Plus size={22} strokeWidth={isActive('/create') ? 2.5 : 2} />
            <span className="text-[10px] font-bold uppercase tracking-widest hidden md:block">Request</span>
          </Link>
        )}
        <Link 
          to="/active-jobs" 
          className={`flex flex-col items-center gap-1 transition-all ${isActive('/active-jobs') ? 'text-amber-700 scale-110' : 'text-stone-400 hover:text-stone-900'}`}
        >
          <ClipboardList size={22} strokeWidth={isActive('/active-jobs') ? 2.5 : 2} />
          <span className="text-[10px] font-bold uppercase tracking-widest hidden md:block">Active</span>
        </Link>
        <Link 
          to="/profile"
          className={`flex flex-col items-center gap-1 transition-all ${isActive('/profile') ? 'text-amber-700 scale-110' : 'text-stone-400 hover:text-stone-900'}`}
        >
          <User size={22} strokeWidth={isActive('/profile') ? 2.5 : 2} />
          <span className="text-[10px] font-bold uppercase tracking-widest hidden md:block">Profile</span>
        </Link>
        <div className="h-4 w-px bg-stone-200 hidden md:block mx-2"></div>
        <button 
          onClick={handleExit}
          className="p-2 text-stone-300 hover:text-rose-600 transition-colors"
        >
          <LogOut size={20} />
        </button>
      </div>
    </nav>
  );
};

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useApp();
  if (!currentUser) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const MainContent: React.FC = () => {
  return (
    <div className="pb-32 pt-8 md:pt-28 md:pb-12 min-h-screen">
      <Routes>
        <Route path="/" element={<MarketingPage />} />
        <Route path="/start" element={<Onboarding />} />
        <Route path="/requester" element={<AuthGuard><RequesterDashboard /></AuthGuard>} />
        <Route path="/artisan" element={<AuthGuard><ArtisanDashboard /></AuthGuard>} />
        <Route path="/create" element={<AuthGuard><CreateRequest /></AuthGuard>} />
        <Route path="/request/:id" element={<AuthGuard><RequestDetail /></AuthGuard>} />
        <Route path="/active-jobs" element={<AuthGuard><ActiveJobs /></AuthGuard>} />
        <Route path="/profile" element={<AuthGuard><Profile /></AuthGuard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AppProvider>
      <HashRouter>
        <div className="min-h-screen bg-[#faf9f6]">
          <Nav />
          <div className="container mx-auto px-4">
            <MainContent />
          </div>
        </div>
      </HashRouter>
    </AppProvider>
  );
};

export default App;
